/*
  © 2024 KondaSoft
  https://www.kondasoft.com
*/

let collectionAbortController = null
let collectionRequestId = 0
const PRICE_FILTER_DEBOUNCE_MS = 420

const setCollectionLoading = (isLoading) => {
  const collectionProducts = document.querySelector('.collection-products')
  const filters = document.querySelector('.collection-filters')

  if (collectionProducts) {
    collectionProducts.classList.toggle('is-loading', isLoading)
    collectionProducts.setAttribute('aria-busy', isLoading ? 'true' : 'false')
  }

  if (filters) {
    filters.classList.toggle('is-loading', isLoading)
    filters.setAttribute('aria-busy', isLoading ? 'true' : 'false')
  }
}

const loadCollection = async (options = {}) => {
  const collectionProducts = document.querySelector('.collection-products')
  const filters = document.querySelector('.collection-filters')
  const requestId = collectionRequestId + 1

  collectionRequestId = requestId

  if (!collectionProducts) return

  if (collectionAbortController) {
    collectionAbortController.abort()
  }

  collectionAbortController = new AbortController()
  setCollectionLoading(true)

  try {
    const response = await fetch(window.location.href, {
      signal: collectionAbortController.signal
    })

    if (!response.ok) {
      throw new Error(`Collection request failed: ${response.status}`)
    }

    const data = await response.text()
    const parser = new DOMParser()
    const newDocument = parser.parseFromString(data, 'text/html')
    const newCollectionProducts = newDocument.querySelector('.collection-products')
    const newFilters = newDocument.querySelector('.collection-filters')

    if (requestId !== collectionRequestId) return

    if (newCollectionProducts) {
      collectionProducts.replaceWith(newCollectionProducts)
    }

    if (filters && newFilters) {
      filters.replaceWith(newFilters)
    }

    window.dispatchEvent(new CustomEvent('kt.collection.loaded'))

    if (options.scroll !== false) {
      const currentCollectionProducts = document.querySelector('.collection-products')
      const navbarHeight = document.querySelector('[id*="__navbar"].sticky-top')?.clientHeight || 0
      currentCollectionProducts.style.scrollMarginTop = `${navbarHeight}px`
      currentCollectionProducts.scrollIntoView()
    }
  } catch (error) {
    if (error.name !== 'AbortError') {
      setCollectionLoading(false)
      window.dispatchEvent(new CustomEvent('kt.collection.error'))
    }
  } finally {
    if (requestId === collectionRequestId) {
      collectionAbortController = null
      setCollectionLoading(false)
    }
  }
}

const updateCollectionUrl = (params, options = {}) => {
  const query = params.toString()
  const url = query ? `${window.location.pathname}?${query}` : window.location.pathname

  if (options.replace) {
    window.history.replaceState({}, '', url)
  } else {
    window.history.pushState({}, '', url)
  }
}

const clearCollectionFilters = () => {
  const params = new URLSearchParams(window.location.search)

  for (const key of [...params.keys()]) {
    if (key.includes('filter.')) {
      params.delete(key)
    }
  }

  params.delete('page')
  updateCollectionUrl(params)
  loadCollection()
}

const isPriceFilterInput = input => input.name.includes('filter.v.price')

const getPriceRangeInputs = form => Array.from(form.querySelectorAll('input[type="number"][name*="filter.v.price"]'))

const validatePriceRange = (form) => {
  const priceInputs = getPriceRangeInputs(form)
  if (!priceInputs.length) return true

  const values = priceInputs.map(input => ({
    input,
    value: input.value === '' ? null : Number(input.value),
    max: Number(input.max || 0)
  }))
  const min = values.find(item => item.input.name.includes('.gte')) || values[0]
  const max = values.find(item => item.input.name.includes('.lte')) || values[1]
  const error = form.querySelector('.collection-price-error')
  const hasNegativeValue = values.some(item => item.value !== null && item.value < 0)
  const exceedsMax = values.some(item => item.value !== null && item.max > 0 && item.value > item.max)
  const isInvalidRange = min?.value !== null && max?.value !== null && min.value > max.value
  const isInvalid = hasNegativeValue || exceedsMax || isInvalidRange

  priceInputs.forEach(input => {
    input.setAttribute('aria-invalid', isInvalid ? 'true' : 'false')
  })

  if (error) {
    error.hidden = !isInvalid
  }

  return !isInvalid
}

class CollectionFilters extends HTMLElement {
  constructor () {
    super()

    this.form = this.querySelector('form')
    this.priceFilterTimeout = null

    this.form.querySelectorAll('input').forEach(input => {
      if (isPriceFilterInput(input)) {
        input.addEventListener('input', () => this.scheduleFilterChange())
        input.addEventListener('change', () => this.scheduleFilterChange())
        input.addEventListener('blur', () => this.submitFilters())
        input.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            event.preventDefault()
            this.submitFilters()
          }
        })
      } else {
        input.addEventListener('change', () => this.submitFilters())
      }
    })

    this.querySelectorAll('.btn-filters-clear-all').forEach(btn => {
      const params = new URLSearchParams(window.location.search)

      for (const key of params.keys()) {
        if (key.includes('filter.')) {
          btn.removeAttribute('hidden')
        }
      }

      btn.addEventListener('click', () => {
        clearCollectionFilters()
      })
    })
  }

  scheduleFilterChange () {
    window.clearTimeout(this.priceFilterTimeout)
    this.priceFilterTimeout = window.setTimeout(() => {
      this.submitFilters()
    }, PRICE_FILTER_DEBOUNCE_MS)
  }

  submitFilters () {
    window.clearTimeout(this.priceFilterTimeout)

    if (!validatePriceRange(this.form)) return

    const params = new URLSearchParams(new FormData(this.form))
    params.delete('page')

    updateCollectionUrl(params)
    loadCollection()
  }
}
customElements.define('collection-filters', CollectionFilters)

class SortBy extends HTMLElement {
  constructor () {
    super()

    this.querySelectorAll('input').forEach(input => {
      input.addEventListener('change', () => {
        this.setUrl(input.value)
        loadCollection()
      })
    })
  }

  setUrl (value) {
    const params = new URLSearchParams(window.location.search)
    params.set('sort_by', value)
    params.delete('page')
    updateCollectionUrl(params)
  }
}
customElements.define('sort-by', SortBy)

class Pagination extends HTMLElement {
  constructor () {
    super()

    this.querySelectorAll('a').forEach(elem => {
      elem.addEventListener('click', (event) => {
        event.preventDefault()

        this.setUrl(elem.dataset.newPage)
        loadCollection()
      })
    })
  }

  setUrl (value) {
    const params = new URLSearchParams(window.location.search)
    params.set('page', value)
    updateCollectionUrl(params)
  }
}
customElements.define('collection-pagination', Pagination)

window.addEventListener('popstate', () => {
  loadCollection({ scroll: false })
})

document.addEventListener('click', (event) => {
  const clearButton = event.target.closest('.js-collection-clear-filters')
  if (!clearButton) return

  clearCollectionFilters()
})
